/**
 * A video note that goes up while it is still being recorded. The recorder hands over each chunk as it
 * arrives; once more than one part's worth is held, the take is reserved (no size yet, it is not known) and
 * every full part goes straight to R2 through the same presigned parts the finished file would use.
 * At Send, `recordNote` takes the session and the part engine sends only what is missing, then completes
 * with the size. A take that never reached a full part reserves nothing, and any failure here only leaves
 * more for Send: the recording itself is never touched (specs/formal/guest-note).
 */
import type { NoteUploadStartResponse, UploadPartResult } from "../../shared/types";
import { type NoteApi, noteCalls } from "./noteUpload";
import { uploadOnePart } from "./videoUpload";

/** The first part's size, before the server has said its own: the part size it answers with today. */
const FIRST_PART_BYTES = 10 * 1024 * 1024;

/** What the take holds at Send: its reserve and the receipts of the parts that already landed. */
export interface LiveSession {
  start: NoteUploadStartResponse;
  receipts: UploadPartResult[];
}

export interface LiveNoteUpload {
  /** One chunk of the recording, in the order the recorder made it. */
  push: (chunk: Blob) => void;
  /** The session at Send, once the parts on their way have landed; null when there is none to finish. Once only. */
  take: () => Promise<LiveSession | null>;
  /** Retake, cancel, leaving: cancel the multipart upload and remove anything stored. */
  abort: (opts?: { keepalive?: boolean }) => Promise<void>;
  /** The upload is over, finished or not: stop watching for the page to close. */
  done: () => void;
}

interface LiveOptions {
  fetchImpl?: typeof fetch;
  delayImpl?: (ms: number) => Promise<void>;
  maxRetriesPerPart?: number;
}

export function startLiveUpload(notes: NoteApi, contentType: string, opts: LiveOptions = {}): LiveNoteUpload {
  let pending: Blob[] = [];
  let pendingBytes = 0;
  let begun = false;
  let session: NoteUploadStartResponse | null = null;
  let stalled = false;
  let taken = false;
  let aborted = false;
  let nextPart = 1;
  const receipts = new Map<number, UploadPartResult>();
  const stop = new AbortController();
  let chain: Promise<void> = Promise.resolve();

  const run = async (): Promise<void> => {
    if (!begun && !aborted && pendingBytes > FIRST_PART_BYTES) {
      begun = true;
      try {
        session = await notes.start({ kind: "video", content_type: contentType });
      } catch {
        stalled = true;
      }
    }
    for (;;) {
      if (!session || stalled || aborted || pendingBytes <= session.part_size) return;
      const all = new Blob(pending);
      const body = all.slice(0, session.part_size);
      const rest = all.slice(session.part_size);
      pending = [rest];
      pendingBytes = rest.size;
      const n = nextPart++;
      try {
        receipts.set(n, await uploadOnePart(noteCalls(session, notes), n, body, { ...opts, signal: stop.signal }));
      } catch {
        stalled = true;
      }
    }
  };

  const ref = () => (session ? { note_id: session.note_id, upload_id: session.upload_id } : null);

  function done(): void {
    window.removeEventListener("pagehide", leaving);
  }
  function leaving(): void {
    void abort({ keepalive: true });
  }

  const abort: LiveNoteUpload["abort"] = async ({ keepalive } = {}) => {
    if (aborted) return;
    aborted = true;
    done();
    stop.abort();
    pending = [];
    pendingBytes = 0;
    // Why no wait when the page is closing: the browser gives no time for it, and a start still on its way is left to the sweep.
    if (!keepalive) await chain;
    const r = ref();
    if (r) await notes.abort(r, { keepalive }).catch(() => {});
  };

  window.addEventListener("pagehide", leaving);

  return {
    push(chunk) {
      if (aborted || taken || stalled) return;
      pending.push(chunk);
      pendingBytes += chunk.size;
      chain = chain.then(run);
    },
    async take() {
      if (aborted || taken) return null;
      taken = true;
      await chain;
      return session ? { start: session, receipts: [...receipts.values()] } : null;
    },
    abort,
    done,
  };
}
